import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { api } from "./lib/api";
import Landing from "./pages/Landing";
import Contact from "./pages/Contact";
import LegalDoc from "./pages/LegalDoc";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import PaymentResult from "./pages/PaymentResult";
import AppLayout from "./layouts/AppLayout";
import Dashboard from "./pages/app/Dashboard";
import Inbox from "./pages/app/Inbox";
import EmailDetail from "./pages/app/EmailDetail";
import Priorites from "./pages/app/Priorites";
import Prospects from "./pages/app/Prospects";
import Relances from "./pages/app/Relances";
import Taches from "./pages/app/Taches";
import AskInbox from "./pages/app/AskInbox";
import Analytics from "./pages/app/Analytics";
import Automatisations from "./pages/app/Automatisations";
import Parametres from "./pages/app/Parametres";
import Compte from "./pages/app/Compte";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="app-loader">
      <div className="lea-orb h-10 w-10 rounded-full animate-pulse-soft" />
    </div>
  );
}

function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const params = new URLSearchParams(window.location.hash.replace("#", ""));
    const sessionId = params.get("session_id");
    api.post("/auth/google/session", { session_id: sessionId })
      .then(({ data }) => {
        setUser(data);
        const intent = sessionStorage.getItem("elvarify-intent-plan");
        const dest = !data.onboarding_done ? "/onboarding"
          : intent ? `/app/compte?subscribe=${intent}` : "/app";
        navigate(dest, { replace: true, state: { user: data } });
      })
      .catch(() => navigate("/login", { replace: true }));
  }, [navigate, setUser]);
  return <Loader />;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarding_done) return <Navigate to="/onboarding" replace />;
  return children;
}

function OnboardingRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboarding_done) return <Navigate to="/app" replace />;
  return <Onboarding />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/mentions-legales" element={<LegalDoc slug="mentions-legales" />} />
      <Route path="/conditions" element={<LegalDoc slug="conditions" />} />
      <Route path="/confidentialite" element={<LegalDoc slug="confidentialite" />} />
      <Route path="/dpa" element={<LegalDoc slug="dpa" />} />
      <Route path="/cookies" element={<LegalDoc slug="cookies" />} />
      <Route path="/login" element={<Auth mode="login" />} />
      <Route path="/register" element={<Auth mode="register" />} />
      <Route path="/forgot-password" element={<Auth mode="forgot" />} />
      <Route path="/reset-password" element={<Auth mode="reset" />} />
      <Route path="/onboarding" element={<OnboardingRoute />} />
      <Route path="/payment/:status" element={<PaymentResult />} />
      <Route path="/app" element={<Protected><AppLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="inbox/:emailId" element={<EmailDetail />} />
        <Route path="priorites" element={<Priorites />} />
        <Route path="prospects" element={<Prospects />} />
        <Route path="relances" element={<Relances />} />
        <Route path="taches" element={<Taches />} />
        <Route path="ask" element={<AskInbox />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="automatisations" element={<Automatisations />} />
        <Route path="parametres" element={<Parametres />} />
        <Route path="compte" element={<Compte />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="bottom-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
